/**
 * NovoOrcamentoPage Component
 * 
 * Página de criação e edição de orçamentos.
 * Permite criar novos orçamentos e editar orçamentos em rascunho.
 * 
 * Funcionalidades:
 * - Busca de produtos
 * - Adição de produtos ao orçamento
 * - Seleção de cliente
 * - Configuração de validade e condições de pagamento
 * - Cálculo automático de totais
 * - Gerenciamento de itens (quantidade, preço, desconto)
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { Plus, Save, ArrowLeft, Trash2, Loader2, Package, Download, ChevronDown, ChevronUp, Building2, UserCheck, Table, Minus, Sparkles, Cpu, CheckCircle2, UserRound, X, CheckCircle, CreditCard } from 'lucide-react'
import { useAuth } from '../../../contexts/AuthContext'
import { useToast } from '../../../hooks/useToast'
import { usePermissions, MODULES, ACTIONS } from '../../../hooks/usePermissions'
import ConfirmModal from '../../../components/ERP/ConfirmModal/ConfirmModal'
import ERPSelectSearchModal, { ERPSelectModalItem, ERPSelectModalEmpty } from '../../../components/ERP/Modal/ERPSelectSearchModal'
import QuotePDFTemplate from '../../../components/ERP/Orcamentos/QuotePDFTemplate'
import { Toast } from '../../../components'
import ERPProductSearch from '../../../components/ERP/ProductSearch'
import { exportQuoteElementToPdf } from '../../../utils/exportQuotePdf'
import { 
  quoteService, 
  customerService,
  specialPricingTablesService,
  paymentTermService
} from '../../../services/erpApi'
import './NovoOrcamentoPage.css'

function formatCustomerDocument(doc) {
  if (!doc) return null
  const digits = String(doc).replace(/\D/g, '')
  if (digits.length === 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  }
  if (digits.length === 14) {
    return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  }
  return doc
}

function NovoOrcamentoPage() {
  const navigate = useNavigate()
  const { id } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const duplicateFromId = searchParams.get('duplicar')
  const { user } = useAuth()
  const { toast, showToast, hideToast } = useToast()
  const { hasPermission } = usePermissions()
  const isEditing = !!id
  const [quoteStatus, setQuoteStatus] = useState('draft')
  const [savedQuoteId, setSavedQuoteId] = useState(null)
  const activeQuoteId = isEditing ? id : savedQuoteId
  const [duplicatingFrom, setDuplicatingFrom] = useState(null)

  // Refs
  const pdfItemsRef = useRef(null)
  const pdfSummaryRef = useRef(null) // mantido por compatibilidade com QuotePDFTemplate
  
  // Estados do orçamento
  const [items, setItems] = useState([])
  const [editingQty, setEditingQty] = useState({})
  const [editingPrice, setEditingPrice] = useState({})
  const [customers, setCustomers] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  
  // Estados do formulário
  const [formData, setFormData] = useState({
    customerId: '',
    validityDate: '',
    paymentCondition: '',
    sellerId: user?.id || '',
    internalObservation: '',
    customerObservation: ''
  })

  // Estado para controlar se a seção de Informações Adicionais está expandida
  const [isAdditionalInfoExpanded, setIsAdditionalInfoExpanded] = useState(false)

  // Estados do modal
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  })
  const [customerModalOpen, setCustomerModalOpen] = useState(false)
  const [customerSearchTerm, setCustomerSearchTerm] = useState('')
  const [loadingCustomers, setLoadingCustomers] = useState(false)
  const [tableModalOpen, setTableModalOpen] = useState(false)
  const [tableSearchTerm, setTableSearchTerm] = useState('')
  const [paymentModalOpen, setPaymentModalOpen] = useState(false)
  const [paymentSearchTerm, setPaymentSearchTerm] = useState('')
  const [generatingPDF, setGeneratingPDF] = useState(false)
  const [pdfQuoteData, setPdfQuoteData] = useState(null)
  const [engineLogs, setEngineLogs] = useState('Iniciando Motor...')

  const loadOrcamento = useCallback(async () => {
    setLoading(true)
    try {
      const response = await quoteService.getById(id)
      if (response.success && response.data) {
        const quote = response.data
        setQuoteStatus(quote.status || 'draft')

        if (quote.status === 'approved') {
          showToast('Orçamento aprovado. Use "Voltar para Rascunho" na página de detalhe para editá-lo.', 'info')
          navigate(`/erp/orcamentos/${id}`, { replace: true })
          return
        }
        
        // Carregar itens separadamente
        const itemsResponse = await quoteService.getById(id)
        let itemsData = []
        
        // Se items vieram na resposta, usar. Senão, buscar separadamente
        if (itemsResponse.success && itemsResponse.data?.items) {
          itemsData = itemsResponse.data.items
        }
        
        const customerId = quote.customer_id || ''

        if (quote.pricing_table_id) {
          setSelectedTableId(quote.pricing_table_id)
          setSelectedTable(quote.pricing_table_name || 'Tabela de preços')
        } else {
          setSelectedTableId(null)
          setSelectedTable('Tabela Geral Medik')
        }
        
        setFormData({
          customerId: customerId,
          validityDate: quote.validity_date || '',
          paymentCondition: quote.payment_condition || '',
          sellerId: quote.seller_id || user?.id || '',
          internalObservation: quote.internal_observation || '',
          customerObservation: quote.customer_observation || ''
        })
        
        // Carregar o cliente específico se houver customerId
        if (customerId) {
          try {
            const customerResponse = await customerService.getById(parseInt(customerId))
            if (customerResponse.success && customerResponse.data) {
              // Adicionar o cliente ao array de clientes para exibição
              setCustomers(prev => {
                const exists = prev.find(c => c.id === parseInt(customerId))
                if (exists) return prev
                return [...prev, customerResponse.data]
              })
            }
          } catch (error) {
            console.error('Erro ao carregar cliente:', error)
          }
        }
        
        // Converter itens para o formato esperado pelo frontend
        if (itemsData && itemsData.length > 0) {
          const formattedItems = itemsData.map(item => ({
            id: item.id,
            product_id: item.product_id,
            product: {
              id: item.product_id,
              name: item.product_name || 'Produto',
              sku: item.sku,
              price: item.unit_price
            },
            quantity: parseFloat(item.quantity),
            unit_price: parseFloat(item.unit_price),
            discount_percent: parseFloat(item.discount_percent || 0),
            discount_amount: parseFloat(item.discount_amount || 0),
            total_price: parseFloat(item.total_price),
            // Preço já gravado no orçamento: não sobrescrever ao trocar tabela
            price_manual: true
          }))
          setItems(formattedItems)
        }
      }
    } catch (error) {
      console.error('Erro ao carregar orçamento:', error)
      showToast('Erro ao carregar orçamento', 'error')
    } finally {
      setLoading(false)
    }
  }, [id, navigate, showToast, user?.id])

  const formatItemsFromQuote = useCallback((itemsData) => {
    if (!itemsData?.length) return []
    return itemsData.map((item, index) => ({
      id: `dup-${item.id || index}-${Date.now()}`,
      product_id: item.product_id,
      product: {
        id: item.product_id,
        name: item.product_name || 'Produto',
        sku: item.sku,
        price: item.unit_price
      },
      quantity: parseFloat(item.quantity),
      unit_price: parseFloat(item.unit_price),
      discount_percent: parseFloat(item.discount_percent || 0),
      discount_amount: parseFloat(item.discount_amount || 0),
      total_price: parseFloat(item.total_price),
      price_manual: true
    }))
  }, [])

  /** Carrega itens/condições de outro orçamento para novo rascunho (outro cliente). */
  const loadDuplicateQuote = useCallback(async (sourceId) => {
    setLoading(true)
    try {
      const response = await quoteService.getById(sourceId)
      if (!response.success || !response.data) {
        throw new Error(response.message || 'Orçamento de origem não encontrado')
      }

      const quote = response.data
      const itemsData = quote.items || []

      if (quote.pricing_table_id) {
        setSelectedTableId(quote.pricing_table_id)
        setSelectedTable(quote.pricing_table_name || 'Tabela de preços')
      } else {
        setSelectedTableId(null)
        setSelectedTable('Tabela Geral Medik')
      }

      const defaultValidity = new Date()
      defaultValidity.setDate(defaultValidity.getDate() + 30)

      setFormData({
        customerId: '',
        validityDate: quote.validity_date || defaultValidity.toISOString().split('T')[0],
        paymentCondition: quote.payment_condition || '',
        sellerId: quote.seller_id || user?.id || '',
        internalObservation: quote.internal_observation || '',
        customerObservation: quote.customer_observation || ''
      })

      setItems(formatItemsFromQuote(itemsData))
      setQuoteStatus('draft')
      setSavedQuoteId(null)
      setDuplicatingFrom(quote.number || quote.id)

      showToast(
        `Orçamento #${quote.number || quote.id} duplicado. Selecione o novo cliente e salve.`,
        'info'
      )
      setCustomerModalOpen(true)
      setSearchParams({}, { replace: true })
    } catch (error) {
      console.error('Erro ao duplicar orçamento:', error)
      showToast(error.message || 'Erro ao duplicar orçamento', 'error')
      setSearchParams({}, { replace: true })
    } finally {
      setLoading(false)
    }
  }, [formatItemsFromQuote, setSearchParams, showToast, user?.id])

  const loadCustomers = useCallback(async (searchTerm = '') => {
    try {
      setLoadingCustomers(true)
      const filters = { status: 'active' }
      if (searchTerm) {
        filters.search = searchTerm
      }
      const response = await customerService.getAll(filters)
      if (response.success) {
        setCustomers(response.data || [])
      } else {
        console.error('Erro na resposta da API:', response)
        setCustomers([])
      }
    } catch (error) {
      console.error('Erro ao carregar clientes:', error)
      showToast('Erro ao carregar clientes', 'error')
      setCustomers([])
    } finally {
      setLoadingCustomers(false)
    }
  }, [showToast])

  const [selectedTable, setSelectedTable] = useState('Tabela Geral Medik')
  const [selectedTableId, setSelectedTableId] = useState(null)
  const [pricingTables, setPricingTables] = useState([])
  const [loadingTables, setLoadingTables] = useState(false)
  const [paymentTerms, setPaymentTerms] = useState([])

  const loadPricingTables = useCallback(async () => {
    setLoadingTables(true)
    try {
      const response = await specialPricingTablesService.getAll({ active: true, limit: 50 })

      if (response.success) {
        const tables = response.data || []
        setPricingTables([
          { id: null, name: 'Tabela Geral Medik', is_default: true },
          ...tables
        ])
      }
    } catch (error) {
      console.error('Erro ao carregar tabelas de preços:', error)
      setPricingTables([{ id: null, name: 'Tabela Geral Medik', is_default: true }])
      setSelectedTable('Tabela Geral Medik')
      setSelectedTableId(null)
    } finally {
      setLoadingTables(false)
    }
  }, [])

  useEffect(() => {
    loadPricingTables()
  }, [loadPricingTables])

  // Ao trocar o cliente, limpar tabela exclusiva de outro cliente
  useEffect(() => {
    if (!selectedTableId) return

    const currentTable = pricingTables.find(t => t.id === selectedTableId)
    if (!currentTable?.client_id) return

    const customerId = parseInt(formData.customerId, 10)
    if (!customerId || currentTable.client_id !== customerId) {
      setSelectedTable('Tabela Geral Medik')
      setSelectedTableId(null)
    }
  }, [formData.customerId, selectedTableId, pricingTables])

  // Carregar condições de pagamento
  const loadPaymentTerms = useCallback(async () => {
    try {
      const response = await paymentTermService.getAll({ active: true })
      if (response.success) {
        setPaymentTerms(response.data || [])
      }
    } catch (error) {
      console.error('Erro ao carregar condições de pagamento:', error)
    }
  }, [])

  useEffect(() => {
    loadPaymentTerms()
  }, [loadPaymentTerms])

  // Carregar dados iniciais
  useEffect(() => {
    if (isEditing) {
      loadOrcamento()
    } else if (duplicateFromId) {
      loadDuplicateQuote(duplicateFromId)
    } else {
      // Definir data de validade padrão (30 dias)
      const defaultValidity = new Date()
      defaultValidity.setDate(defaultValidity.getDate() + 30)
      setFormData(prev => ({
        ...prev,
        validityDate: defaultValidity.toISOString().split('T')[0]
      }))
    }
  }, [isEditing, loadOrcamento, duplicateFromId, loadDuplicateQuote])

  // Carregar clientes quando o modal for aberto ou quando o termo de busca mudar
  useEffect(() => {
    if (!customerModalOpen) return

    const timeoutId = setTimeout(() => {
      loadCustomers(customerSearchTerm)
    }, customerSearchTerm.trim() ? 300 : 0)

    return () => clearTimeout(timeoutId)
  }, [customerModalOpen, customerSearchTerm, loadCustomers])

  const handleSelectTable = useCallback(async (table) => {
    setSelectedTable(table.name)
    setSelectedTableId(table.id)
    setTableModalOpen(false)
    setTableSearchTerm('')

    if (!formData.customerId || items.length === 0) return

    try {
      let updatedItems
      let updatedCount = 0

      if (table.id) {
        updatedItems = await Promise.all(
          items.map(async (item) => {
            if (item.price_manual) return item
            try {
              const priceResponse = await specialPricingTablesService.calculatePrice(
                parseInt(formData.customerId),
                item.product_id,
                table.id
              )
              if (priceResponse.success && priceResponse.data?.price !== null && priceResponse.data?.price !== undefined) {
                const newPrice = parseFloat(priceResponse.data.price)
                const discountAmount = (newPrice * item.quantity * item.discount_percent) / 100
                const totalPrice = (newPrice * item.quantity) - discountAmount
                updatedCount += 1
                return {
                  ...item,
                  unit_price: newPrice,
                  discount_amount: discountAmount,
                  total_price: totalPrice,
                  price_manual: false
                }
              }

              const defaultPrice = item.product?.price || item.unit_price
              const discountAmount = (defaultPrice * item.quantity * item.discount_percent) / 100
              const totalPrice = (defaultPrice * item.quantity) - discountAmount
              updatedCount += 1
              return {
                ...item,
                unit_price: defaultPrice,
                discount_amount: discountAmount,
                total_price: totalPrice,
                price_manual: false
              }
            } catch (error) {
              console.error(`Erro ao buscar preço para produto ${item.product_id}:`, error)
              return item
            }
          })
        )
        if (updatedCount > 0) {
          showToast('Preços atualizados conforme a tabela (itens com preço manual mantidos)', 'success')
        } else {
          showToast('Itens com preço manual mantidos; nenhum preço de tabela aplicado', 'info')
        }
      } else {
        updatedItems = items.map(item => {
          if (item.price_manual) return item
          const defaultPrice = item.product?.price || item.unit_price
          const discountAmount = (defaultPrice * item.quantity * item.discount_percent) / 100
          const totalPrice = (defaultPrice * item.quantity) - discountAmount
          updatedCount += 1
          return {
            ...item,
            unit_price: defaultPrice,
            discount_amount: discountAmount,
            total_price: totalPrice,
            price_manual: false
          }
        })
        if (updatedCount > 0) {
          showToast('Preços atualizados para tabela padrão (itens com preço manual mantidos)', 'success')
        }
      }

      setItems(updatedItems)
    } catch (error) {
      console.error('Erro ao atualizar preços:', error)
      showToast('Erro ao atualizar preços da tabela', 'error')
    }
  }, [formData.customerId, items, showToast])

  const closeAllSelectModals = useCallback(() => {
    setCustomerModalOpen(false)
    setCustomerSearchTerm('')
    setTableModalOpen(false)
    setTableSearchTerm('')
    setPaymentModalOpen(false)
    setPaymentSearchTerm('')
  }, [])

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && (customerModalOpen || tableModalOpen || paymentModalOpen)) {
        closeAllSelectModals()
      }
    }

    if (customerModalOpen || tableModalOpen || paymentModalOpen) {
      document.addEventListener('keydown', handleEscape)
      return () => document.removeEventListener('keydown', handleEscape)
    }
  }, [customerModalOpen, tableModalOpen, paymentModalOpen, closeAllSelectModals])


  // Adicionar produto diretamente do ERPProductSearch
  const handleAddProduct = useCallback(async (product) => {
    // Verificar se o produto já está na lista
    if (items.find(i => i.product_id === product.id)) {
      showToast('Produto já está na lista', 'warning')
      return
    }

    let priceValue = product.price ? parseFloat(String(product.price)) : 0
    
    // Se houver tabela selecionada e cliente, buscar preço da tabela
    if (selectedTableId && formData.customerId) {
      try {
        const priceResponse = await specialPricingTablesService.calculatePrice(
          parseInt(formData.customerId),
          product.id,
          selectedTableId // Passar o tableId da tabela selecionada
        )
        if (priceResponse.success && priceResponse.data?.price !== null && priceResponse.data?.price !== undefined) {
          priceValue = parseFloat(priceResponse.data.price)
        }
      } catch (error) {
        console.error('Erro ao buscar preço da tabela:', error)
        // Usar preço padrão do produto em caso de erro
      }
    }

    const qty = 1
    const discount = 0
    const discountAmount = (priceValue * qty * discount) / 100
    const totalPrice = (priceValue * qty) - discountAmount

    const newItem = {
      product_id: product.id,
      product: product, // Para exibição
      quantity: qty,
      unit_price: priceValue,
      discount_percent: discount,
      discount_amount: discountAmount,
      total_price: totalPrice,
      price_manual: false
    }

    setItems(prev => [...prev, newItem])
  }, [items, showToast, selectedTableId, formData.customerId])

  // Atualizar preços quando tabela mudar
  const updatePricesFromTable = useCallback(async () => {
    if (!selectedTableId || !formData.customerId || items.length === 0) {
      return
    }

    try {
      const updatedItems = await Promise.all(
        items.map(async (item) => {
          if (item.price_manual) return item
          try {
            const priceResponse = await specialPricingTablesService.calculatePrice(
              parseInt(formData.customerId),
              item.product_id,
              selectedTableId // Passar o tableId da tabela selecionada
            )
            if (priceResponse.success && priceResponse.data?.price !== null && priceResponse.data?.price !== undefined) {
              const newPrice = parseFloat(priceResponse.data.price)
              const discountAmount = (newPrice * item.quantity * item.discount_percent) / 100
              const totalPrice = (newPrice * item.quantity) - discountAmount
              return {
                ...item,
                unit_price: newPrice,
                discount_amount: discountAmount,
                total_price: totalPrice,
                price_manual: false
              }
            }
          } catch (error) {
            console.error(`Erro ao buscar preço para produto ${item.product_id}:`, error)
          }
          return item
        })
      )
      setItems(updatedItems)
    } catch (error) {
      console.error('Erro ao atualizar preços:', error)
      showToast('Erro ao atualizar preços da tabela', 'error')
    }
  }, [selectedTableId, formData.customerId, items, showToast])

  // Atualizar preços quando tabela mudar (apenas quando selecionar uma tabela diferente da padrão)
  useEffect(() => {
    if (selectedTableId && formData.customerId && items.length > 0) {
      const timeoutId = setTimeout(() => {
        updatePricesFromTable()
      }, 300) // Debounce para evitar múltiplas chamadas
      return () => clearTimeout(timeoutId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTableId]) // Apenas quando a tabela mudar

  // Remover item
  const handleRemoveItem = (index) => {
    setItems(prev => prev.filter((_, i) => i !== index))
  }

  // Atualizar quantidade do item
  const calcItemTotalPrice = (unitPrice, quantity, discountPercent) => {
    const qty = Math.max(1, quantity)
    const discountAmount = (unitPrice * qty * discountPercent) / 100
    return (unitPrice * qty) - discountAmount
  }

  const updateItemQty = (productId, delta) => {
    setEditingQty((prev) => {
      if (!prev[productId]) return prev
      const next = { ...prev }
      delete next[productId]
      return next
    })
    setItems(prev => prev.map(item => {
      if (item.product_id !== productId) return item
      const quantity = Math.max(1, item.quantity + delta)
      return {
        ...item,
        quantity,
        total_price: calcItemTotalPrice(item.unit_price, quantity, item.discount_percent)
      }
    }))
  }

  const setItemQty = (productId, rawQty) => {
    const parsed = parseInt(rawQty, 10)
    if (Number.isNaN(parsed)) return

    const quantity = Math.max(1, parsed)
    setItems(prev => prev.map(item => {
      if (item.product_id !== productId) return item
      return {
        ...item,
        quantity,
        total_price: calcItemTotalPrice(item.unit_price, quantity, item.discount_percent)
      }
    }))
  }

  const parseUnitPriceInput = (rawPrice) => {
    if (rawPrice === '' || rawPrice == null) return null
    const normalized = String(rawPrice).trim().replace(/\s/g, '').replace(',', '.')
    if (normalized === '' || normalized === '.') return null
    const parsed = parseFloat(normalized)
    if (Number.isNaN(parsed) || parsed < 0) return null
    return Math.round(parsed * 100) / 100
  }

  const setItemUnitPrice = (productId, rawPrice) => {
    const parsed = parseUnitPriceInput(rawPrice)
    if (parsed == null) return

    setItems(prev => prev.map(item => {
      if (item.product_id !== productId) return item
      const discountAmount = (parsed * item.quantity * item.discount_percent) / 100
      return {
        ...item,
        unit_price: parsed,
        discount_amount: discountAmount,
        total_price: calcItemTotalPrice(parsed, item.quantity, item.discount_percent),
        price_manual: true
      }
    }))
  }

  // Salvar orçamento (sempre rascunho nesta tela)
  const handleSave = async ({ quiet = false } = {}) => {
    if (!formData.customerId) {
      if (!quiet) showToast('Selecione um cliente', 'warning')
      return false
    }

    if (!formData.validityDate) {
      if (!quiet) showToast('Informe a data de validade', 'warning')
      return false
    }

    if (items.length === 0) {
      if (!quiet) showToast('Adicione pelo menos um item ao orçamento', 'warning')
      return false
    }

    setSaving(true)
    try {
      // Calcular totais
      const totalAmount = items.reduce((sum, item) => {
        const itemTotal = parseFloat(item.unit_price || 0) * parseFloat(item.quantity || 0);
        return sum + (isNaN(itemTotal) ? 0 : itemTotal);
      }, 0);
      
      const discountAmount = items.reduce((sum, item) => {
        const itemDiscount = parseFloat(item.discount_amount || 0);
        return sum + (isNaN(itemDiscount) ? 0 : itemDiscount);
      }, 0);
      
      // Garantir que os valores são números válidos
      const safeTotalAmount = isNaN(totalAmount) ? 0 : Number(totalAmount);
      const safeDiscountAmount = isNaN(discountAmount) ? 0 : Number(discountAmount);
      // Criar objeto data apenas com campos permitidos
      const data = {
        customerId: parseInt(formData.customerId),
        validityDate: formData.validityDate,
        paymentCondition: formData.paymentCondition || null,
        sellerId: formData.sellerId ? parseInt(formData.sellerId) : null,
        internalObservation: formData.internalObservation || null,
        customerObservation: formData.customerObservation || null,
        status: 'draft',
        totalAmount: safeTotalAmount,
        discountAmount: safeDiscountAmount,
        pricingTableId: selectedTableId || null
        // finalAmount não deve ser enviado, é calculado no backend
      }

      let quoteId = activeQuoteId
      let response

      if (activeQuoteId) {
        response = await quoteService.update(activeQuoteId, data)
        if (!response.success) {
          throw new Error(response.message || 'Erro ao atualizar orçamento')
        }

        const currentQuote = await quoteService.getById(activeQuoteId)
        if (currentQuote.success && currentQuote.data?.items) {
          for (const item of currentQuote.data.items) {
            try {
              await quoteService.deleteItem(activeQuoteId, item.id)
            } catch (err) {
              console.warn('Erro ao deletar item:', err)
            }
          }
        }

        for (const item of items) {
          await quoteService.addItem(activeQuoteId, {
            productId: item.product_id,
            quantity: item.quantity,
            unitPrice: item.unit_price,
            discountPercent: item.discount_percent || 0,
            discountAmount: item.discount_amount || 0
          })
        }
      } else {
        response = await quoteService.create(data)
        if (!response.success) {
          throw new Error(response.message || 'Erro ao criar orçamento')
        }

        quoteId = response.data.id
        setSavedQuoteId(quoteId)

        for (const item of items) {
          await quoteService.addItem(quoteId, {
            productId: item.product_id,
            quantity: item.quantity,
            unitPrice: item.unit_price,
            discountPercent: item.discount_percent || 0,
            discountAmount: item.discount_amount || 0
          })
        }

        navigate(`/erp/orcamentos/${quoteId}/editar`, { replace: true })
      }

      if (!quiet) {
        showToast(
          activeQuoteId ? 'Orçamento atualizado com sucesso!' : 'Orçamento salvo como rascunho!',
          'success'
        )
        if (isEditing) {
          navigate('/erp/orcamentos')
        }
      }
      return quoteId
    } catch (error) {
      console.error('Erro ao salvar orçamento:', error)
      if (!quiet) {
        showToast(
          error.response?.data?.message || error.message || 'Erro ao salvar orçamento',
          'error'
        )
      }
      return false
    } finally {
      setSaving(false)
    }
  }

  const handleApprove = () => {
    if (!activeQuoteId) {
      showToast('Salve o orçamento antes de aprovar', 'warning')
      return
    }

    setConfirmModal({
      isOpen: true,
      title: 'Aprovar Orçamento',
      message: 'Deseja salvar e aprovar este orçamento? Você será redirecionado para a pré-venda.',
      onConfirm: async () => {
        setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })
        const quoteId = await handleSave({ quiet: true })
        if (!quoteId) return

        setSaving(true)
        try {
          const response = await quoteService.approve(quoteId)
          if (response.success) {
            setQuoteStatus('approved')
            showToast('Orçamento aprovado com sucesso!', 'success')
            navigate(`/erp/vendas/pre-venda?quoteId=${quoteId}`)
          } else {
            showToast(response.message || 'Erro ao aprovar orçamento', 'error')
          }
        } catch (error) {
          console.error('Erro ao aprovar orçamento:', error)
          showToast(error.response?.data?.message || error.message || 'Erro ao aprovar orçamento', 'error')
        } finally {
          setSaving(false)
        }
      }
    })
  }

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0)
  }


  // Função para gerar PDF do orçamento
  const handleGeneratePDF = async () => {
    try {
    // Obter dados do cliente selecionado
    const selectedCustomer = customers.find(c => c.id === parseInt(formData.customerId))

    // Calcular totais
    const totalAmount = items.reduce((sum, item) => sum + (item.unit_price * item.quantity), 0)
    const discountAmount = items.reduce((sum, item) => sum + (item.discount_amount || 0), 0)
    const finalAmount = totalAmount - discountAmount

      // Preparar dados do orçamento para o PDF
      const quoteData = {
        id: isEditing ? id : 'NOVO',
        customer: selectedCustomer || null,
        validityDate: formData.validityDate,
        paymentCondition: formData.paymentCondition,
        customerObservation: formData.customerObservation,
        items: items.map(item => ({
          product_name: item.product?.name || item.product_name || 'Produto',
          quantity: item.quantity,
          unit_price: item.unit_price,
          discount_percent: item.discount_percent,
          total_price: item.total_price
        })),
        subtotal: totalAmount,
        discount: discountAmount,
        total: finalAmount,
        created_at: new Date().toISOString()
      }

      setPdfQuoteData(quoteData)
      setGeneratingPDF(true)
      setEngineLogs('Carregando dados do orçamento...')
      
      // Aguardar um pouco para garantir que os componentes foram renderizados
      await new Promise(r => setTimeout(r, 500))
      
      if (!pdfItemsRef.current) {
        showToast('Erro ao gerar PDF. Tente novamente.', 'error')
        setGeneratingPDF(false)
        return
      }

      setEngineLogs('Renderizando documento...')
      await new Promise(r => setTimeout(r, 200))
      setEngineLogs('Gerando PDF...')

      const fileName = `Orcamento_${isEditing ? id : 'Novo'}_${new Date().getFullYear()}_${Date.now()}.pdf`
      await exportQuoteElementToPdf(pdfItemsRef.current, fileName)
      
      setEngineLogs('Concluído!')
      await new Promise(r => setTimeout(r, 200))
      
      showToast('PDF gerado com sucesso!', 'success')
    } catch (error) {
      console.error('Erro ao gerar PDF:', error)
      showToast('Erro ao gerar PDF. Tente novamente.', 'error')
    } finally {
      setGeneratingPDF(false)
      setPdfQuoteData(null)
    }
  }


  if (loading) {
    return <div className="novo-orcamento-loading">Carregando orçamento...</div>
  }

  const selectedCustomer = customers.find(c => c.id === parseInt(formData.customerId))
  const subtotal = items.reduce((acc, curr) => acc + (curr.unit_price * curr.quantity), 0)
  const totalDiscount = items.reduce((acc, curr) => acc + (curr.discount_amount || 0), 0)
  const finalTotal = subtotal - totalDiscount

  return (
    <div className="novo-orcamento-page">
      {/* Header Bar */}
      <div className="novo-orcamento-header">
        <div className="novo-orcamento-header-left">
                <button
            className="novo-orcamento-btn-back" 
            onClick={() => navigate('/erp/orcamentos')}
          >
            <ArrowLeft size={24} />
                </button>
          <div>
            <h1 className="novo-orcamento-title">
              {isEditing ? 'Editar' : duplicatingFrom ? 'Duplicar' : 'Novo'}{' '}
              <span className="novo-orcamento-title-accent">Orçamento</span>
            </h1>
            <p className="novo-orcamento-subtitle">
              {duplicatingFrom
                ? `Baseado no #${duplicatingFrom} — escolha o novo cliente`
                : 'Venda Assistida B2B'}
            </p>
              </div>
            </div>
        <div className="novo-orcamento-header-right">
          <button
            type="button"
            className="btn-print btn-print--cancel"
            onClick={() => navigate('/erp/orcamentos')}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn-print"
            onClick={handleGeneratePDF}
            disabled={saving || items.length === 0}
          >
            <Download size={18} />
            Baixar PDF
          </button>
          <button
            type="button"
            className="btn-print btn-print--save"
            onClick={handleSave}
            disabled={saving || !formData.customerId || !formData.validityDate || items.length === 0}
          >
            {saving ? <Loader2 size={18} className="spinner" /> : <Save size={18} />}
            Salvar
          </button>
          {isEditing && quoteStatus === 'draft' && hasPermission(MODULES.QUOTES, ACTIONS.QUOTES_APPROVE) && (
            <button
              type="button"
              className="btn-print btn-print--approve"
              onClick={handleApprove}
              disabled={saving || !formData.customerId || !formData.validityDate || items.length === 0}
            >
              {saving ? <Loader2 size={18} className="spinner" /> : <CheckCircle2 size={18} />}
              Aprovar
            </button>
          )}
        </div>
      </div>

      <div className="novo-orcamento-grid">
        {/* Coluna Esquerda: Dados do Cliente */}
        <div className="novo-orcamento-col-left">
          <div className="novo-orcamento-col-left-content">
            <div className="novo-orcamento-card">
            <div className="novo-orcamento-card-bg" />
            <h3 className="novo-orcamento-card-title">
              <UserCheck size={16} className="novo-orcamento-card-icon" />
              Dados do Solicitante
            </h3>

            <div className="novo-orcamento-card-content">
              {/* Seleção de Cliente */}
              <div className="novo-orcamento-field">
                <label className="novo-orcamento-label">Buscar Cliente</label>
                <div className="novo-orcamento-input-wrapper">
                  <Building2 size={18} className="novo-orcamento-input-icon" />
                  <input
                    type="text"
                    readOnly
                    className="novo-orcamento-input novo-orcamento-input-clickable"
                    placeholder="Nome da Instituição..."
                    value={selectedCustomer?.name || ''}
                    onClick={() => setCustomerModalOpen(true)}
                  />
                  <ChevronDown size={14} className="novo-orcamento-input-chevron" />
                </div>
              </div>

              {/* Seletor de Tabela de Preços */}
              <div className="novo-orcamento-field">
                <div className="novo-orcamento-field-header">
                  <label className="novo-orcamento-label">Tabela de Preços</label>
                  {selectedTable !== 'Tabela Geral Medik' && (
                    <span className="novo-orcamento-badge-custom">Customizada</span>
                  )}
                </div>
                <div className="novo-orcamento-input-wrapper">
                  <Table size={18} className="novo-orcamento-input-icon" />
                  <input
                    type="text"
                    readOnly
                    className="novo-orcamento-input novo-orcamento-input-clickable"
                    placeholder="Selecionar tabela..."
                    value={selectedTable}
                    onClick={() => setTableModalOpen(true)}
                  />
                  <ChevronDown size={14} className="novo-orcamento-input-chevron" />
                </div>
                <p className="novo-orcamento-field-hint">
                  {!selectedTableId || selectedTable === 'Tabela Geral Medik'
                    ? 'Aplicando preços padrão de catálogo.' 
                    : `Vínculo ativo: ${selectedTable}.`}
                </p>
              </div>

              {/* Validade e Entrega */}
              <div className="novo-orcamento-field-grid">
                <div className="novo-orcamento-field">
                  <label className="novo-orcamento-label">Validade (Dias)</label>
                    <input
                    type="number" 
                    className="novo-orcamento-input"
                    value={formData.validityDate ? Math.max(1, Math.ceil((new Date(formData.validityDate).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24))) : '30'}
                      onChange={(e) => {
                      const days = parseInt(e.target.value) || 30
                      const newDate = new Date()
                      newDate.setDate(newDate.getDate() + days)
                      setFormData(prev => ({ ...prev, validityDate: newDate.toISOString().split('T')[0] }))
                      }}
                    />
                  </div>
                <div className="novo-orcamento-field">
                  <label className="novo-orcamento-label">Data de Validade</label>
                    <input
                    type="date"
                    className="novo-orcamento-input"
                    value={formData.validityDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, validityDate: e.target.value }))}
                    required
                    />
                  </div>
              </div>

              {/* Condição de Pagamento */}
              <div className="novo-orcamento-field">
                <label className="novo-orcamento-label">Condição de Pagamento</label>
                <div className="novo-orcamento-input-wrapper">
                  <CreditCard size={18} className="novo-orcamento-input-icon" />
                  <input
                    type="text"
                    readOnly
                    className="novo-orcamento-input novo-orcamento-input-clickable"
                    placeholder="Selecione a condição de pagamento..."
                    value={formData.paymentCondition || ''}
                    onClick={() => setPaymentModalOpen(true)}
                  />
                  <ChevronDown size={14} className="novo-orcamento-input-chevron" />
                </div>
              </div>
            </div>
          </div>

            {/* Assistente Medik */}
            {selectedCustomer && (
              <div className="novo-orcamento-assistant">
                <div className="novo-orcamento-assistant-bg" />
                <div className="novo-orcamento-assistant-content">
                  <div className="novo-orcamento-assistant-header">
                    <Sparkles size={20} className="novo-orcamento-assistant-icon" />
                    <span className="novo-orcamento-assistant-title">Assistente Medik</span>
                  </div>
                  <p className="novo-orcamento-assistant-text">
                    Aqui será registra as informações importantes sobre pagamentos do cliente. Prazo para pagamento <strong>15 dias</strong> e pagamento via <strong>PIX</strong>.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Card de Produtos */}
          <div className="novo-orcamento-products-card">
            {/* Busca de produtos com ERPProductSearch */}
            <div className="novo-orcamento-products-search">
              <ERPProductSearch onSelect={handleAddProduct} />
            </div>

            {/* Lista de Itens */}
            <div className="novo-orcamento-items-container">
              {items.length > 0 ? (
                <table className="novo-orcamento-items-table">
                <thead>
                  <tr>
                      <th>Item / Catálogo</th>
                      <th className="novo-orcamento-th-center">Quantidade</th>
                      <th className="novo-orcamento-th-right">Preço Unit.</th>
                      <th className="novo-orcamento-th-right">Subtotal</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                      <tr key={item.id || index} className="novo-orcamento-item-row">
                        <td>
                          <div className="novo-orcamento-item-product">
                            <div className="novo-orcamento-item-image">
                              {item.product?.image_url ? (
                                <img src={item.product.image_url} alt={item.product.name} />
                              ) : (
                                <Package size={20} />
                              )}
                            </div>
                            <div className="novo-orcamento-item-info">
                              <p className="novo-orcamento-item-name">
                                {item.product?.name || item.product_name || 'Produto'}
                              </p>
                              {item.product?.sku && (
                                <p className="novo-orcamento-item-sku">{item.product.sku}</p>
                              )}
                            </div>
                          </div>
                      </td>
                      <td>
                          <div className="novo-orcamento-item-qty">
                            <button 
                              onClick={() => updateItemQty(item.product_id, -1)}
                              className="novo-orcamento-item-qty-btn"
                            >
                              <Minus size={14} />
                            </button>
                            <input
                              type="number"
                              min={1}
                              step={1}
                              className="novo-orcamento-item-qty-value"
                              value={editingQty[item.product_id] ?? item.quantity}
                              onChange={(e) => {
                                const { value } = e.target
                                setEditingQty((prev) => ({ ...prev, [item.product_id]: value }))
                                if (value !== '' && !Number.isNaN(parseInt(value, 10))) {
                                  setItemQty(item.product_id, value)
                                }
                              }}
                              onBlur={(e) => {
                                const value = e.target.value.trim()
                                setItemQty(item.product_id, value && parseInt(value, 10) >= 1 ? value : '1')
                                setEditingQty((prev) => {
                                  const next = { ...prev }
                                  delete next[item.product_id]
                                  return next
                                })
                              }}
                              aria-label={`Quantidade de ${item.product?.name || item.product_name || 'produto'}`}
                            />
                            <button 
                              onClick={() => updateItemQty(item.product_id, 1)}
                              className="novo-orcamento-item-qty-btn"
                            >
                              <Plus size={14} />
                            </button>
                          </div>
                      </td>
                        <td className="novo-orcamento-td-right">
                          <div className={`novo-orcamento-item-price-wrap${item.price_manual ? ' is-manual' : ''}`}>
                            <span className="novo-orcamento-item-price-prefix">R$</span>
                            <input
                              type="number"
                              min={0}
                              step="0.01"
                              className="novo-orcamento-item-price-input"
                              value={editingPrice[item.product_id] ?? item.unit_price}
                              onChange={(e) => {
                                const { value } = e.target
                                setEditingPrice((prev) => ({ ...prev, [item.product_id]: value }))
                                if (value !== '' && parseUnitPriceInput(value) != null) {
                                  setItemUnitPrice(item.product_id, value)
                                }
                              }}
                              onBlur={(e) => {
                                const value = e.target.value.trim()
                                const parsed = parseUnitPriceInput(value)
                                setItemUnitPrice(item.product_id, parsed != null ? String(parsed) : '0')
                                setEditingPrice((prev) => {
                                  const next = { ...prev }
                                  delete next[item.product_id]
                                  return next
                                })
                              }}
                              aria-label={`Preço unitário de ${item.product?.name || item.product_name || 'produto'}`}
                              title={item.price_manual ? 'Preço manual (não sobrescrito pela tabela)' : 'Preço da tabela/produto — edite livremente'}
                            />
                          </div>
                      </td>
                        <td className="novo-orcamento-td-right">
                          <span className="novo-orcamento-item-total">{formatCurrency(item.total_price)}</span>
                        </td>
                        <td className="novo-orcamento-td-right">
                        <button
                          onClick={() => handleRemoveItem(index)}
                            className="novo-orcamento-item-remove"
                        >
                            <Trash2 size={20} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              ) : (
                <div className="novo-orcamento-items-empty">
                  <Package size={48} />
                  <p className="novo-orcamento-items-empty-text">Nenhum item adicionado ao orçamento</p>
            </div>
          )}
            </div>

          {/* Totais */}
            <div className="novo-orcamento-totals">
              <div className="novo-orcamento-totals-left">
                <div className="novo-orcamento-total-item">
                  <p className="novo-orcamento-total-label">Subtotal Bruto</p>
                  <p className="novo-orcamento-total-value">{formatCurrency(subtotal)}</p>
            </div>
                <div className="novo-orcamento-total-item">
                  <p className="novo-orcamento-total-label-discount">Desconto Comercial</p>
                  <p className="novo-orcamento-total-value-discount">- {formatCurrency(totalDiscount)}</p>
            </div>
              </div>
              <div className="novo-orcamento-totals-right">
                <p className="novo-orcamento-total-label-final">Total Final Orçado</p>
                <p className="novo-orcamento-total-value-final">{formatCurrency(finalTotal)}</p>
              </div>
            </div>
            </div>
          </div>
        </div>

      {/* Seção de Informações Adicionais */}
      <div className="novo-orcamento-additional-info">
        <div className="novo-orcamento-additional-header" onClick={() => setIsAdditionalInfoExpanded(!isAdditionalInfoExpanded)}>
          <h2 className="novo-orcamento-additional-title">Informações Adicionais</h2>
          <button
            type="button"
            className="novo-orcamento-additional-toggle"
            aria-label={isAdditionalInfoExpanded ? 'Minimizar' : 'Expandir'}
          >
            {isAdditionalInfoExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>
        </div>
        {isAdditionalInfoExpanded && (
          <div className="novo-orcamento-additional-content">
            <div className="novo-orcamento-additional-field">
              <label className="novo-orcamento-label">Observações para o Cliente</label>
              <textarea
                className="novo-orcamento-textarea"
                value={formData.customerObservation}
                onChange={(e) => setFormData(prev => ({ ...prev, customerObservation: e.target.value }))}
                rows="3"
                placeholder="Observações que aparecerão no orçamento para o cliente"
              />
            </div>
          </div>
        )}
      </div>

      {/* Modais e componentes auxiliares */}
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
      />

      <Toast
        message={toast.message}
        type={toast.type}
        show={toast.show}
        onClose={hideToast}
        duration={4000}
      />

      {/* Modal de Seleção de Cliente */}
      <ERPSelectSearchModal
        isOpen={customerModalOpen}
        onClose={() => {
          setCustomerModalOpen(false)
          setCustomerSearchTerm('')
        }}
        title="Selecionar Cliente"
        subtitle="Escolha o cliente para este orçamento"
        icon={<UserRound size={24} />}
        searchPlaceholder="Buscar por nome ou documento..."
        searchTerm={customerSearchTerm}
        onSearchChange={setCustomerSearchTerm}
        loading={loadingCustomers}
        footer={selectedCustomer ? (
          <button
            type="button"
            className="erp-select-modal-btn-secondary"
            onClick={() => {
              setFormData(prev => ({ ...prev, customerId: '' }))
              setCustomerModalOpen(false)
              setCustomerSearchTerm('')
            }}
          >
            <X size={18} />
            Remover Cliente
          </button>
        ) : null}
      >
        {loadingCustomers ? (
          <ERPSelectModalEmpty
            icon={<Loader2 size={32} className="spinning" />}
            message="Buscando clientes..."
          />
        ) : customers.length > 0 ? (
          customers.map(customer => (
            <ERPSelectModalItem
              key={customer.id}
              selected={formData.customerId === customer.id.toString()}
              onClick={() => {
                setFormData(prev => ({ ...prev, customerId: customer.id.toString() }))
                setCustomerModalOpen(false)
                setCustomerSearchTerm('')
              }}
              avatar={<UserRound size={20} />}
              name={customer.name || 'Sem nome'}
              detail={formatCustomerDocument(customer.cpf_cnpj || customer.document || customer.cpfCnpj)}
              checkIcon={<CheckCircle size={20} className="erp-select-modal-item-check" />}
            />
          ))
        ) : (
          <ERPSelectModalEmpty
            icon={<UserRound size={48} />}
            message={customerSearchTerm.trim() ? 'Nenhum cliente encontrado' : 'Digite para buscar clientes'}
          />
        )}
      </ERPSelectSearchModal>

      {/* Modal de Seleção de Tabela de Preços */}
      <ERPSelectSearchModal
        isOpen={tableModalOpen}
        onClose={() => {
          setTableModalOpen(false)
          setTableSearchTerm('')
        }}
        title="Selecionar Tabela de Preços"
        subtitle="Tabelas de preços especiais"
        icon={<Table size={24} />}
        searchPlaceholder="Buscar tabela de preços..."
        searchTerm={tableSearchTerm}
        onSearchChange={setTableSearchTerm}
        loading={loadingTables}
      >
        {loadingTables ? (
          <ERPSelectModalEmpty
            icon={<Loader2 size={32} className="spinning" />}
            message="Buscando tabelas..."
          />
        ) : (() => {
          const filteredTables = pricingTables.filter(t =>
            t.name?.toLowerCase().includes(tableSearchTerm.toLowerCase())
          )
          if (filteredTables.length === 0) {
            return (
              <ERPSelectModalEmpty
                icon={<Table size={48} />}
                message={tableSearchTerm.trim() ? 'Nenhuma tabela encontrada' : 'Nenhuma tabela cadastrada'}
              />
            )
          }
          return filteredTables.map(table => (
            <ERPSelectModalItem
              key={table.id || 'default'}
              selected={selectedTable === table.name}
              onClick={() => handleSelectTable(table)}
              avatar={<Table size={20} />}
              name={table.name}
              detail={table.is_default ? 'Usar preços padrão de catálogo' : null}
              checkIcon={<CheckCircle size={20} className="erp-select-modal-item-check" />}
            />
          ))
        })()}
      </ERPSelectSearchModal>

      {/* Modal de Condição de Pagamento */}
      <ERPSelectSearchModal
        isOpen={paymentModalOpen}
        onClose={() => {
          setPaymentModalOpen(false)
          setPaymentSearchTerm('')
        }}
        title="Condição de Pagamento"
        subtitle="Selecione a condição para este orçamento"
        icon={<CreditCard size={24} />}
        searchPlaceholder="Buscar condição de pagamento..."
        searchTerm={paymentSearchTerm}
        onSearchChange={setPaymentSearchTerm}
      >
        {(() => {
          const filteredTerms = paymentTerms.filter(term => {
            const searchLower = paymentSearchTerm.toLowerCase()
            return term.name?.toLowerCase().includes(searchLower) ||
              term.description?.toLowerCase().includes(searchLower)
          })
          if (filteredTerms.length === 0) {
            return (
              <ERPSelectModalEmpty
                icon={<CreditCard size={48} />}
                message={paymentSearchTerm.trim() ? 'Nenhuma condição encontrada' : 'Nenhuma condição cadastrada'}
              />
            )
          }
          return filteredTerms.map(term => (
            <ERPSelectModalItem
              key={term.id}
              selected={formData.paymentCondition === term.name}
              onClick={() => {
                setFormData(prev => ({ ...prev, paymentCondition: term.name }))
                setPaymentModalOpen(false)
                setPaymentSearchTerm('')
              }}
              avatar={<CreditCard size={20} />}
              name={term.name}
              detail={term.description}
              checkIcon={<CheckCircle size={20} className="erp-select-modal-item-check" />}
            />
          ))
        })()}
      </ERPSelectSearchModal>

      {/* Overlay de Geração de PDF */}
      {generatingPDF && (
        <div className="follow-up-pdf-overlay">
          <div className="follow-up-pdf-overlay-content">
            <div className="follow-up-pdf-icon-wrapper">
              <Cpu size={32} className="spinner" />
              <div className="follow-up-pdf-scan" />
            </div>
            <div>
              <h4 className="follow-up-pdf-title">Gerando PDF</h4>
              <p className="follow-up-pdf-subtitle">Exportando Orçamento</p>
            </div>
            <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div className="follow-up-pdf-log">
                <p className="follow-up-pdf-log-text">{engineLogs}</p>
              </div>
              <div className="follow-up-pdf-progress-bar">
                <div className="follow-up-pdf-progress-fill" />
              </div>
            </div>
          </div>
        </div>
      )}

      {pdfQuoteData && (
        <QuotePDFTemplate
          pdfQuoteData={pdfQuoteData}
          itemsRef={pdfItemsRef}
          summaryRef={pdfSummaryRef}
        />
      )}
    </div>
  )
}

export default NovoOrcamentoPage

